
import { useEffect, useState } from "react";
import { SheetTab } from "./SheetTab";

/** "4s ago" / "2m ago" / "1h ago" — for the Generate AI timestamp. */
function formatAgo(ts: number): string {
  const delta = Math.max(0, Date.now() - ts);
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

/**
 * Bottom bar — workbook tab on the left, plus preview-row count/limit and AI
 * generation controls on the right. Lives at the very bottom; the grid above
 * it is the main canvas.
 */
export function GridBottomBar({
  activeTab = "workbook",
  onSelectTab,
  previewLoading,
  previewCount,
  sourceCount,
  sourceTotal,
  previewLimit,
  onChangePreviewLimit,
  aiSkipped,
  pendingAICells = 0,
  profileHasAIRules,
  onTestAI,
  testingAI,
  testedAt,
  testedCount,
}: {
  activeTab?: "workbook" | "source";
  onSelectTab?: (tab: "workbook" | "source") => void;
  previewLoading: boolean;
  previewCount: number;
  sourceCount: number;
  sourceTotal: number;
  previewLimit: number;
  onChangePreviewLimit: (n: number) => void;
  aiSkipped: boolean;
  /** AI cells in view still waiting to be generated. */
  pendingAICells?: number;
  profileHasAIRules: boolean;
  onTestAI: (n: 1 | 2 | 3) => Promise<void>;
  testingAI: boolean;
  testedAt: number | null;
  testedCount: number;
}) {
  const [testAIRows, setTestAIRows] = useState<1 | 2 | 3>(1);
  const [limitDraft, setLimitDraft] = useState(String(previewLimit));
  const [, setNowTick] = useState(0);

  useEffect(() => setLimitDraft(String(previewLimit)), [previewLimit]);
  useEffect(() => {
    if (testedAt == null) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [testedAt]);

  const commitLimit = () => {
    const parsed = Number(limitDraft);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setLimitDraft(String(previewLimit));
      return;
    }
    onChangePreviewLimit(Math.min(50, Math.max(1, Math.floor(parsed))));
  };

  return (
    <div className="flex shrink-0 items-center gap-1 border-t border-slate-200 dark:border-border bg-slate-100 dark:bg-surface-2 px-2 py-1">
      <SheetTab
        label="Workbook"
        active={activeTab === "workbook"}
        badge={previewLoading ? "…" : String(previewCount)}
        onClick={() => onSelectTab?.("workbook")}
      />
      <SheetTab
        label="Source"
        active={activeTab === "source"}
        badge={String(sourceCount)}
        onClick={() => onSelectTab?.("source")}
      />

      <div className="ml-auto flex items-center gap-3 pr-1">
        <span className="text-[10.5px] text-slate-500 dark:text-slate-400">
          Source rows {sourceCount}
          {sourceTotal > sourceCount ? "+" : ""}
        </span>
        <label className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-400">
          <span>Rows</span>
          <input
            type="number"
            min={1}
            max={50}
            value={limitDraft}
            onChange={(e) => setLimitDraft(e.target.value)}
            onBlur={commitLimit}
            onKeyDown={(e) => e.key === "Enter" && commitLimit()}
            className="w-14 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-right text-[11px] tabular-nums focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:border-border dark:bg-surface dark:focus:border-slate-400 dark:focus:ring-slate-600"
          />
        </label>
        {profileHasAIRules && (
          <div className="flex items-center gap-1.5 border-l border-slate-300 dark:border-border pl-3">
            {aiSkipped && pendingAICells > 0 && (
              <span
                className="text-[10.5px] text-violet-700 dark:text-violet-300"
                title="AI runs only when you ask: ▶ on a column, or Generate AI here. Generated output is saved in the workbook and reused by the export."
              >
                {pendingAICells} AI cell{pendingAICells === 1 ? "" : "s"} to generate
              </span>
            )}
            <span className="text-[11px] text-slate-600 dark:text-slate-400">Generate AI on first</span>
            <select
              value={testAIRows}
              onChange={(e) => setTestAIRows(Number(e.target.value) as 1 | 2 | 3)}
              disabled={testingAI}
              className="rounded border border-violet-200 dark:border-violet-900/60 bg-white dark:bg-surface px-1 py-0.5 text-[11px] tabular-nums text-violet-800 dark:text-violet-300 focus:border-violet-500 dark:focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-300 dark:focus:ring-violet-700 disabled:opacity-50"
              aria-label="Rows to generate AI on"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
            <button
              type="button"
              onClick={() => onTestAI(testAIRows)}
              disabled={testingAI}
              title="Run every AI rule on the first rows and save the output in the workbook. Export generates whatever is still missing."
              className="inline-flex items-center gap-1 rounded bg-violet-600 px-2 py-0.5 text-[11px] font-medium text-white transition hover:bg-violet-700 disabled:opacity-50"
            >
              {testingAI ? (
                <span aria-hidden className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-violet-200 dark:border-violet-900/60 border-t-transparent" />
              ) : (
                <span aria-hidden>✨</span>
              )}
              {testingAI ? "Generating…" : "Generate AI"}
            </button>
            {testedAt != null && !testingAI && (
              <span className="text-[10.5px] text-violet-700 dark:text-violet-300">
                {testedCount} row{testedCount === 1 ? "" : "s"} · {formatAgo(testedAt)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
